// The base no-unused-vars rule does not see type-only uses.
/* eslint no-unused-vars: "off", "@typescript-eslint/no-unused-vars": "warn" */
import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Header, SpaceBetween, Table } from '@amzn/awsui-components-react-v3/polaris';
import { RetryAlert } from '../../Common/ErrorMessages';
import { convertDateTimePretty } from '../../../utils/ReadableConverter';
import { LoadClient, useLoaded } from '../ScopingModels/useLoaded';
import * as goldenDatasets from './goldenDatasets';

type VersionSummary = goldenDatasets.GoldenDatasetVersionSummary;

/** A dataset's versions, newest first as Pricing lists them, each linking to its snapshot. */
const GoldenDatasetHistory = ({
  apiClient,
  datasetId,
}: {
  apiClient: LoadClient;
  datasetId: string;
}): JSX.Element => {
  const versions = useLoaded<VersionSummary[]>(
    apiClient,
    goldenDatasets.versionsPath(datasetId),
    goldenDatasets.describeError
  );

  const columnDefinitions = [
    {
      id: 'version',
      header: 'Version',
      cell: (item: VersionSummary): JSX.Element => (
        <Link to={goldenDatasets.versionRoute(datasetId, item.version)}>{item.version}</Link>
      ),
    },
    {
      id: 'change',
      header: 'Change',
      cell: (item: VersionSummary): string => goldenDatasets.CHANGE_LABELS[item.change],
    },
    { id: 'rows', header: 'Rows', cell: (item: VersionSummary): number => item.rowCount },
    {
      id: 'changed',
      header: 'Changed',
      cell: (item: VersionSummary): string =>
        `${convertDateTimePretty(item.updatedAt)} by ${item.author}`,
    },
  ];

  return (
    <SpaceBetween size="m">
      {versions.errors.length > 0 && (
        <RetryAlert
          id="goldenDatasetHistoryError"
          header="Could not load the version history"
          messages={versions.errors}
          retryId="retryGoldenDatasetHistory"
          onRetry={versions.reload}
        />
      )}
      <Table
        id="goldenDatasetHistory"
        items={versions.value || []}
        columnDefinitions={columnDefinitions}
        trackBy={(item: VersionSummary): string => String(item.version)}
        loading={versions.value === null && !versions.missing && versions.errors.length === 0}
        loadingText="Loading the version history"
        // After a failed load the retry alert explains the empty table.
        empty={versions.errors.length === 0 && <Box textAlign="center">No versions</Box>}
        header={<Header variant="h2">History</Header>}
      />
    </SpaceBetween>
  );
};

export default GoldenDatasetHistory;
